import { useEffect, useMemo, useState } from 'react';
import { RJSFValidationError } from '@rjsf/utils';
import { OrderRequestSchema, Product } from '../../../types';
import { isAdvancedOrderFormEnabled, OrderRequestPreferences } from '../../../store/User';
import { buildRequestSchema } from './requestSchema';
import { pruneFormDataToSchema, DISCONNECT_REQUEST } from './serviceRequestModel';

interface SchemaFormInput {
  requestType: string;
  productSelection: Product | null;
  orderRequestSchemas: OrderRequestSchema[];
  orderRequestPrefs: OrderRequestPreferences;
}

/** The advanced (schema-driven) layout: the chosen product family's schema, trimmed for the request type, and its values kept in step with it. */
export function useSchemaForm({
  requestType,
  productSelection,
  orderRequestSchemas,
  orderRequestPrefs
}: SchemaFormInput) {
  const [showAdvancedLayout, setShowAdvancedLayout] = useState<boolean>(false);
  const [orderRequestSchema, setOrderRequestSchema] = useState<OrderRequestSchema | null>(null);
  const [schemaFormData, setSchemaFormData] = useState<Record<string, unknown>>({});
  const [schemaFormErrors, setSchemaFormErrors] = useState<RJSFValidationError[]>([]);

  useEffect(() => {
    if (productSelection && orderRequestSchemas.length > 0 && requestType !== DISCONNECT_REQUEST) {
      const schema = orderRequestSchemas.find((schema) => schema.product_family === productSelection.family);
      setOrderRequestSchema(schema || null);
      setShowAdvancedLayout(isAdvancedOrderFormEnabled(orderRequestPrefs) && schema !== undefined);
      // schemaFormData is reconciled by the prune effect below, not wiped here.
    } else {
      setShowAdvancedLayout(false);
      setOrderRequestSchema(null);
    }
  }, [orderRequestPrefs, productSelection, orderRequestSchemas, requestType]);

  const requestSchema = useMemo(
    () => buildRequestSchema(orderRequestSchema, requestType, productSelection),
    [orderRequestSchema, requestType, productSelection]
  );

  const dynamicUiSchema = useMemo(() => {
    if (!requestSchema) return undefined;
    const uiSchema = requestSchema.schema.ui_schema;
    if (!uiSchema) return uiSchema;
    if (schemaFormData.billing_same_as_install) {
      return {
        ...uiSchema,
        billing_address: { ...(uiSchema.billing_address || {}), 'ui:disabled': true }
      };
    }
    return uiSchema;
  }, [requestSchema, schemaFormData.billing_same_as_install]);

  // Reconcile schemaFormData with the active product's schema: keep shared
  // fields, drop ones it no longer renders, and clear it when no schema is
  // active so stale advanced fields are never submitted.
  useEffect(() => {
    setSchemaFormData((prev) => {
      const pruned = pruneFormDataToSchema(prev, requestSchema);
      return Object.keys(pruned).length === Object.keys(prev).length ? prev : pruned;
    });
  }, [requestSchema]);

  const handleSchemaFormChange = (formData: Record<string, unknown> | null) => {
    setSchemaFormData(formData || {});
  };

  const resetSchemaForm = () => {
    setShowAdvancedLayout(false);
    setOrderRequestSchema(null);
    setSchemaFormData({});
    setSchemaFormErrors([]);
  };

  return {
    showAdvancedLayout,
    orderRequestSchema,
    requestSchema,
    dynamicUiSchema,
    schemaFormData,
    setSchemaFormData,
    schemaFormErrors,
    setSchemaFormErrors,
    handleSchemaFormChange,
    resetSchemaForm
  };
}
